/**
 * Resolves the owner bank account ("DestinationAccount") recorded against a
 * sent summary.
 *
 * Extracted from the four report components (ReportTransactionsSimple/Full and
 * their Clone variants), which each had this logic inlined twice.
 */

export type OwnerLike = {
  OwnerID?: number | string | null;
  BankAccount?: string | null;
};

export type OwnerId = number | string | null | undefined;

/**
 * CURRENT PRODUCTION BEHAVIOUR - retained verbatim so the accompanying test can
 * demonstrate the defect before it is fixed. Do not call this from components.
 *
 * Two defects:
 *  1. Reads from the Redux owners-list slice, which is only populated by
 *     visiting the /owners page. Reaching a report any other way leaves it
 *     empty, so the filter matches nothing and this yields "".
 *  2. `[0]` takes the FIRST match rather than the report's own owner, and the
 *     template literal stringifies null into the literal text "null".
 */
export function resolveDestinationAccountLegacy(
  owners: OwnerLike[] | null | undefined,
  ownerIds: OwnerId[]
): string {
  return (
    (owners ?? [])
      .filter((owner: any) => ownerIds.includes(owner.OwnerID))
      .map((owner: any) => `${owner?.BankAccount}`)[0] || ""
  );
}

/**
 * Normalises a stored BankAccount into the value we are willing to persist.
 *
 * BankAccount is an overloaded free-text column - most owners hold "FV"
 * (invoiced rather than paid by transfer), others hold an IBAN, and some hold
 * notes like "---" or "czekam". Those are all preserved as-is; only genuinely
 * absent values and the strings produced by stringifying null/undefined are
 * flattened to "".
 */
function normaliseBankAccount(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  // Guards against `${null}` / `${undefined}`, which previously reached the
  // database as real-looking account numbers.
  if (text === "null" || text === "undefined") return "";
  return text;
}

/**
 * Resolves the destination account for a report.
 *
 * `curUser` is the owner the report belongs to. AppContainer refetches it from
 * /users/profile/:ownerId on every route change, so it is always present on a
 * /reports/:ownerId route and always carries BankAccount - unlike the owners
 * list slice, and unlike /users/list it applies no Role filter.
 *
 * The owners list is consulted only as a fallback, and only when it identifies
 * exactly one owner, so it can never silently substitute another owner's
 * account number.
 */
export function resolveDestinationAccount(
  curUser: OwnerLike | null | undefined,
  owners?: OwnerLike[] | null,
  ownerIds: OwnerId[] = []
): string {
  const fromCurUser = normaliseBankAccount(curUser?.BankAccount);
  if (fromCurUser) return fromCurUser;

  const matches = (owners ?? []).filter(
    (owner) =>
      owner?.OwnerID !== null &&
      owner?.OwnerID !== undefined &&
      ownerIds.some((id) => String(id) === String(owner.OwnerID))
  );
  if (matches.length === 1) return normaliseBankAccount(matches[0].BankAccount);

  return "";
}
